import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { StarButton } from "@/components/Watchlist";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Suggestion = ({ stock, active, onPick }) => {
  const up = stock.change_percent >= 0;
  return (
    <li className="gs-li">
      <button
        className={`gs-item ${active ? "active" : ""}`}
        onMouseDown={(e) => { e.preventDefault(); onPick(stock.symbol); }}
        data-testid={`global-search-result-${stock.symbol}`}
      >
        <span className="gs-symbol">{stock.symbol}</span>
        <span className="gs-name">{stock.name}</span>
        <span className="gs-price">{stock.price == null ? "—" : `$${stock.price.toFixed(2)}`}</span>
        {stock.price != null && <span className={`gs-change ${up ? "positive" : "negative"}`}>{up ? "+" : ""}{stock.change_percent.toFixed(2)}%</span>}
      </button>
      <StarButton symbol={stock.symbol} size={16} />
    </li>
  );
};

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const seq = useRef(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const q = query.trim();
    const id = ++seq.current;
    if (!q) { setResults([]); setLoading(false); return undefined; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/stocks/search/${encodeURIComponent(q)}`);
        if (id === seq.current) { setResults(res.data.results || []); setActive(0); }
      } catch (e) {
        if (id === seq.current) setResults([]);
      } finally {
        if (id === seq.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT") { e.preventDefault(); inputRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pick = (symbol) => {
    setQuery(""); setResults([]); setOpen(false);
    inputRef.current?.blur();
    navigate(`/stock/${symbol}`);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && results[active]) pick(results[active].symbol);
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
  };

  const showPanel = open && query.trim().length > 0;

  return (
    <div className="global-search" data-testid="global-search">
      <Search className="gs-icon" size={17} />
      <input
        ref={inputRef}
        className="gs-input"
        placeholder="Search any stock…"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        data-testid="global-search-input"
        aria-label="Search stocks"
      />
      {query && (
        <button className="gs-clear" onMouseDown={(e) => { e.preventDefault(); setQuery(""); }} data-testid="global-search-clear" aria-label="Clear search">
          <X size={15} />
        </button>
      )}
      {showPanel && (
        <div className="gs-panel" data-testid="global-search-panel">
          {loading && results.length === 0 ? (
            <p className="gs-empty">Searching the herd…</p>
          ) : results.length === 0 ? (
            <p className="gs-empty" data-testid="global-search-empty">No stocks found for "{query}"</p>
          ) : (
            <ul className="gs-list">
              {results.map((s, i) => <Suggestion key={s.symbol} stock={s} active={i === active} onPick={pick} />)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
